import { Emitter, type Event } from '@patsnap/tahiti-core';

export interface DocumentSavedEvent {
    documentId: string;
    version: number;
    timestamp: Date;
    contentHtml?: string;
    silent?: boolean;
}

export interface DocumentTitleRefreshedEvent {
    documentId: string;
    projectId: string;
    title: string;
    actionId: string;
}

/** 文档领域事件的只读订阅边界。 */
export interface DocumentEventSource {
    readonly onSaved: Event<DocumentSavedEvent>;
    readonly onTitleRefreshed: Event<DocumentTitleRefreshedEvent>;
}

/** 文档领域事件的发布边界。 */
export interface DocumentEventPublisher {
    /** 发布文档保存完成事实。 */
    publishSaved(event: DocumentSavedEvent): void;
    /** 发布文档标题刷新完成事实。 */
    publishTitleRefreshed(event: DocumentTitleRefreshedEvent): void;
}

export interface DocumentEventsOptions {
    onListenerError?(
        error: unknown,
        event: DocumentSavedEvent | DocumentTitleRefreshedEvent,
    ): void;
}

/** 创建可隔离测试的文档领域事件服务。 */
export function createDocumentEvents(
    options: DocumentEventsOptions = {},
): DocumentEventSource & DocumentEventPublisher {
    const savedEmitter = new Emitter<DocumentSavedEvent>({
        onListenerError: options.onListenerError,
    });
    const titleRefreshedEmitter = new Emitter<DocumentTitleRefreshedEvent>({
        onListenerError: options.onListenerError,
    });

    return {
        onSaved: savedEmitter.event,
        onTitleRefreshed: titleRefreshedEmitter.event,
        publishSaved: event => savedEmitter.fire(event),
        publishTitleRefreshed: event => titleRefreshedEmitter.fire(event),
    };
}

/** 应用级文档领域事件服务。 */
export const documentEvents = createDocumentEvents({
    onListenerError: error => console.error('[DocumentEvents] listener failed:', error),
});
